import { inject, Injectable } from '@angular/core';

import { AccountDataEvents, MatrixClient, Method } from 'matrix-js-sdk';
import { AppConfigService } from '../config/app-config.service';
import { SETTINGS } from '../session/session.service';

export enum AuthMode {
  ALLOW_ALL = 'allow all',
  BLOCK_ALL = 'block all'
}

export interface DeletionPeriodConfigurations {
  timeUntilDeletion: number;
  isActivated: boolean;
}

export interface EventNotificationConfigurations {
  isActivated: boolean;
  forInvites: { viaMail: boolean; viaTaskManagement: boolean };
  forMessages: { viaMail: boolean; viaTaskManagement: boolean };
}

export interface SettingsConfiguration {
  notifications: boolean;
  receipts: boolean;
  typingNotifications: boolean;
  inactivity: number;
  tracking: boolean;
  trackingPopUp: boolean;
  avatarIcon: string;
  lastClientVersion: string;
  presencePrivacy: boolean;
}

/**
 * Interface über die verschiedenen Attribute der Einstellungen
 */
export interface SettingsAttributes {
  settingsConfigurations: SettingsConfiguration;
  deletionPeriodConfigurations: DeletionPeriodConfigurations;
  eventNotificationConfigurations: EventNotificationConfigurations;
}

/**
 * Default-Konfigurationen
 */
export const DEFAULT_CONFIGURATION: SettingsAttributes = {
  settingsConfigurations: {
    notifications: false,
    receipts: false,
    typingNotifications: false,
    inactivity: 3600000,
    tracking: false,
    trackingPopUp: false,
    lastClientVersion: '',
    avatarIcon: '',
    presencePrivacy: false
  },
  deletionPeriodConfigurations: {
    timeUntilDeletion: 6,
    isActivated: true
  },
  eventNotificationConfigurations: {
    isActivated: false,
    forInvites: { viaMail: false, viaTaskManagement: false },
    forMessages: { viaMail: false, viaTaskManagement: false }
  }
};

export const SETTINGS_KEY = 'tim.sbclient.settings' as keyof AccountDataEvents;
export const DELETE_PERIOD_KEY = 'de.gematik.tim.account.deletePeriod.v1' as keyof AccountDataEvents;
export const EVENT_NOTIFICATION_KEY = 'de.gematik.tim.account.notifications.v1' as keyof AccountDataEvents;

/**
 * Anzeigeklasse für die Einstellungen des Clients
 *
 * Hierdurch erfüllte AFO's:
 *
 * A_26016 - Basiseinstellung vorgeben
 * A_25045-02 - Funktionsumfang der Berechtigungskonfiguration
 * A_26390 - Schema der Berechtigungskonfiguration
 * A_25595 - Deaktivierbarkeit zuvor aktivierten Trackings und Reportings
 */
export class SettingsOriginator {
  state: SettingsAttributes;

  constructor(settings: SettingsAttributes) {
    this.state = structuredClone(settings);
  }

  public setState(settings: SettingsAttributes): void {
    this.state = structuredClone(settings);
  }
}

/**
 * Singleton-Klasse der Einstellungen
 * Begründung: Siehe oben
 */
export class Settings {
  private static uniqueInstance: Settings;

  settings: SettingsAttributes;

  private constructor(settingsAttributes: SettingsAttributes) {
    this.settings = structuredClone(settingsAttributes);
  }

  public static getInstance(): SettingsAttributes {
    this.uniqueInstance ??= new Settings(DEFAULT_CONFIGURATION);
    return this.uniqueInstance.settings;
  }

  public static setInstance(settingsAttributes: SettingsAttributes): void {
    this.uniqueInstance = new Settings(settingsAttributes);
  }
}

@Injectable({
  providedIn: 'root'
})
/**
 * Service für das Bündeln von Methoden, die Logik auf Basis der Settings ausführen.
 *
 * Hierdurch erfüllte AFO's:
 * A_25046 - Durchsetzung der Berechtigungskonfiguration - Client
 */
export class SettingsService {
  private readonly config: AppConfigService = inject(AppConfigService);

  /**
   * Hole die gespeicherten Einstellungen vom HomeServer.
   */
  async loadSettings(matrixClient: MatrixClient): Promise<void> {
    const settingsAttributes: SettingsAttributes = {
      settingsConfigurations: { ...DEFAULT_CONFIGURATION.settingsConfigurations },
      deletionPeriodConfigurations: { ...DEFAULT_CONFIGURATION.deletionPeriodConfigurations },
      eventNotificationConfigurations: { ...DEFAULT_CONFIGURATION.eventNotificationConfigurations }
    };

    try {
      const [settingsFromServer, deletionPeriodFromServer, eventNotificationFromServer] = await Promise.all([
        matrixClient.getAccountDataFromServer(SETTINGS_KEY),
        matrixClient.getAccountDataFromServer(DELETE_PERIOD_KEY),
        matrixClient.getAccountDataFromServer(EVENT_NOTIFICATION_KEY)
        /*
        matrixClient.getAccountDataFromServer(
          'de.gematik.tim.account.permissionconfig.pro.v1' as keyof AccountDataEvents
        )) as PermissionConfiguration;
        */
      ]);

      // Setze gefetchte Einstellungen, sonst belasse default
      if (
        settingsFromServer &&
        this.hasObj2AllKeysOfObj1(DEFAULT_CONFIGURATION.settingsConfigurations, settingsFromServer)
      ) {
        settingsAttributes.settingsConfigurations = settingsFromServer as SettingsConfiguration;
        console.info('Settings aus Homeserver übernommen');
      } else {
        console.info('Default settings gesetzt');
      }
      if (
        deletionPeriodFromServer &&
        this.hasObj2AllKeysOfObj1(DEFAULT_CONFIGURATION.deletionPeriodConfigurations, deletionPeriodFromServer)
      ) {
        settingsAttributes.deletionPeriodConfigurations = deletionPeriodFromServer as DeletionPeriodConfigurations;
        console.log('DeletionPeriod aus Homeserver übernommen');
      } else {
        console.info('Default deletion period gesetzt');
      }
      if (
        eventNotificationFromServer &&
        this.hasObj2AllKeysOfObj1(DEFAULT_CONFIGURATION.eventNotificationConfigurations, eventNotificationFromServer)
      ) {
        settingsAttributes.eventNotificationConfigurations =
          eventNotificationFromServer as EventNotificationConfigurations;
        console.info('EventNotification aus Homeserver übernommen');
      } else {
        console.info('Default event notification gesetzt');
      }
    } catch (error) {
      console.warn('Fehler beim Abfragen der Konfigurationseinstellungen vom Server', error);
    }

    // Settings im SessionStorage speichern
    sessionStorage.setItem(SETTINGS, JSON.stringify(settingsAttributes));

    // Settings in der Instanz setzen
    Settings.setInstance(settingsAttributes);
  }

  /**
   * Speichert die Einstellungen
   */
  async saveSettings(matrixClient: MatrixClient, settingsAttributes: SettingsAttributes): Promise<void> {
    await Promise.all([
      matrixClient.setAccountData(SETTINGS_KEY, settingsAttributes.settingsConfigurations),
      matrixClient.setAccountData(DELETE_PERIOD_KEY, settingsAttributes.deletionPeriodConfigurations),
      matrixClient.setAccountData(EVENT_NOTIFICATION_KEY, settingsAttributes.eventNotificationConfigurations)
    ]);

    sessionStorage.setItem(SETTINGS, JSON.stringify(settingsAttributes));
    Settings.setInstance(settingsAttributes);
  }

  // Was hat das mit Settings zu tun? TODO Besseren Platz finden (refactorn)!
  async getDeletionPeriod(client: MatrixClient): Promise<DeleteResponse> {
    const userId = client.getUserId();
    if (!userId) throw new Error('No userId available');

    // OpenID Token holen
    const oidcResponse = await client.http.authedRequest<OpenIdTokenResponse>(
      Method.Post,
      `/user/${encodeURIComponent(userId)}/openid/request_token`,
      undefined,
      {}
    );
    const accessToken = oidcResponse.access_token;

    // Request an den Löschfrist-Endpunkt
    const url = `${this.config.get('regDienstUrl')}/client/${this.config.get('homeserverHostname')}/loeschfrist`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    return response.json() as Promise<DeleteResponse>;
  }

  /**
   * Helferfunktion: Prüft, ob alle Schlüssel von obj1 in Obj2 vorhanden sind
   */
  private hasObj2AllKeysOfObj1(obj1: object, obj2: object): boolean {
    let hasAllKeys = true;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    keys1.forEach(it => {
      if (!keys2.includes(it)) {
        hasAllKeys = false;
        console.log('fehlender Key', it);
      }
    });
    return hasAllKeys;
  }
}

export interface DeleteResponse {
  vorgangsId: string;
  returnCode: string;
  loeschFrist: number;
}

interface OpenIdTokenResponse {
  access_token: string;
  token_type: string;
  matrix_server_name: string;
  expires_in: number;
}
